import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEOMeta from './SEOMeta';
import PublicNavbar from './PublicNavbar';
import {
  BarChart2,
  ArrowRight,
  CheckCircle,
  Star,
  Clock,
  Users,
  Building2,
  ChevronRight,
  Award,
} from 'lucide-react';

// ── Beta case studies ──────────────────────────────────────────────────────────
const CASE_STUDIES = [
  {
    initials: 'MW',
    name: 'Marcus W.',
    location: 'Columbus, OH',
    units: '4 units',
    tier: 'Reactive → Developing',
    tierColor: 'text-orange-600',
    score: 78,
    quote: 'My score was a wake-up call I didn\'t expect. NivasaPro.ai showed me exactly where the gaps were dragging my operation down. It gave me a clear path to close them. Within 60 days things were running differently.',
    result: 'Maintenance requests resolved 40% faster after closing the gaps NivasaPro.ai identified.',
    stars: 5,
  },
  {
    initials: 'PS',
    name: 'Priya S.',
    location: 'Dublin, OH',
    units: '11 units',
    tier: 'Developing → Managed',
    tierColor: 'text-yellow-600',
    score: 52,
    quote: 'I thought I was doing well until I saw my score. NivasaPro.ai surfaced the gaps that were quietly costing me time every week. It helped me close them. Things I\'d been putting off for months got handled in two weeks.',
    result: 'Saved ~5 hours per week on routine follow-ups once NivasaPro.ai bridged the gaps.',
    stars: 5,
  },
  {
    initials: 'DT',
    name: 'David T.',
    location: 'Westerville, OH',
    units: '23 units',
    tier: 'Managed → Optimized',
    tierColor: 'text-cyan-600',
    score: 34,
    quote: 'Running 23 units I assumed I was in good shape. The ROMI Score told a different story. NivasaPro.ai gave me the tools to close every gap it found. Same-day action, no guesswork.',
    result: 'Zero compliance-related issues in the quarter after NivasaPro.ai helped close the gaps.',
    stars: 5,
  },
];

// ── How it works steps ─────────────────────────────────────────────────────────
const STEPS = [
  { n: '1', title: 'Start a chat with Niva', desc: 'No forms, no signup, no credit card. Open the assessment and Niva greets you immediately.' },
  { n: '2', title: 'Answer 8–12 plain-English questions', desc: 'Niva asks about your portfolio, how you spend your time, your tools, and where the friction is. Takes about 3 minutes.' },
  { n: '3', title: 'Receive your full ROMI Score™ report', desc: 'Your score (0–100), maturity tier, dimension breakdown, a key insight, and 3 specific recommendations, delivered instantly.' },
];

// ── FAQ ────────────────────────────────────────────────────────────────────────
const FAQS = [
  { q: 'Is the ROMI Score really free?', a: 'Yes, completely free. No account, no credit card, no upsell during the assessment. We built it because landlords deserve to know where they stand before committing to any software.' },
  { q: 'How long does it take?', a: 'Most landlords finish in 2–4 minutes. Niva asks focused questions and doesn\'t repeat itself. There\'s no minimum or maximum. You go at your own pace.' },
  { q: 'What is the ROMI Score based on?', a: 'ROMI stands for Rental Operations Maturity Index. The scoring model was developed from patterns across independent landlords managing 1–50 units. It evaluates multiple operational dimensions, covering how you manage your time, tenants, maintenance, finances, documentation, and technology, and derives a maturity level unique to your portfolio.' },
  { q: 'Is my data kept private?', a: 'Yes. Your assessment responses are used only to generate your score. We don\'t sell your data or use it to contact you without permission. You can optionally provide your email to receive your report. That\'s the only information we store.' },
  { q: 'What if I already use property management software?', a: 'The ROMI Score assesses your operational maturity, not your tool choice. Landlords using Buildium, AppFolio, or spreadsheets all benefit. The assessment tells you how well your current setup is working, not which product to switch to.' },
];

// ── JSON-LD schema ─────────────────────────────────────────────────────────────
const romiSchema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      name: 'ROMI Score™: Free Rental Operations Maturity Assessment',
      url: 'https://nivasapro.ai/romi-score',
      description: 'A free 3-minute AI assessment that tells independent landlords where their rental operation stands across five dimensions and provides a clear roadmap to the next maturity tier.',
    },
    {
      '@type': 'FAQPage',
      mainEntity: FAQS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
    {
      '@type': 'SoftwareApplication',
      name: 'NivasaPro ROMI Score™',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
      description: 'AI-powered rental operations maturity assessment for independent landlords.',
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '54',
      },
    },
  ],
};

// ── Component ──────────────────────────────────────────────────────────────────
const RomiScorePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <SEOMeta
        title="ROMI Score™: Free Rental Operations Maturity Assessment for Landlords"
        description="Get your free ROMI Score™. A 3-minute AI assessment that tells independent landlords where their rental operation stands and exactly what to improve next."
        canonical="/romi-score"
        schema={romiSchema}
      />
      <PublicNavbar activeRoute="/romi-score" />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="bg-gradient-to-b from-gray-950 to-gray-900 text-white pt-20 pb-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-cyan-500/15 border border-cyan-500/30 rounded-full px-4 py-1.5 text-cyan-300 text-sm font-medium mb-8">
            <Award className="h-4 w-4" />
            Free · 3 minutes · No signup required
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            Find Out Where Your{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
              Rental Operation Really Stands
            </span>
          </h1>

          <p className="text-lg md:text-xl text-gray-300 leading-relaxed max-w-2xl mx-auto mb-10">
            The <strong className="text-white">ROMI Score™</strong> (Rental Operations Maturity Index) is a free AI-powered
            assessment that shows independent landlords exactly where their operation sits on a
            five-tier maturity curve, and what to fix first.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
            <Link
              to="/niva-romi-score"
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-bold px-8 py-4 rounded-xl text-base transition-colors shadow-lg shadow-cyan-500/25"
            >
              Get Your Free ROMI Score™ <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href="#how-it-works"
              className="text-gray-400 hover:text-white text-sm font-medium transition-colors"
            >
              How it works ↓
            </a>
          </div>

          {/* Social proof bar */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
            <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-cyan-500" />54+ landlords assessed</span>
            <span className="flex items-center gap-1.5"><Star className="h-4 w-4 text-amber-400 fill-amber-400" />4.9 average rating</span>
            <span className="flex items-center gap-1.5"><Clock className="h-4 w-4 text-cyan-500" />Avg. 3 min completion</span>
            <span className="flex items-center gap-1.5"><Building2 className="h-4 w-4 text-cyan-500" />1–50 unit portfolios</span>
          </div>
        </div>
      </section>

      {/* ── What is the ROMI Score ─────────────────────────────────────────── */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Is the ROMI Score™?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Most landlords don't realise they have a problem until it's too late: a tenant who
              churned, a repair bill that spiralled, or a weekend ruined by an "emergency" that
              wasn't. The ROMI Score makes the invisible visible.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-5 text-gray-700 leading-relaxed">
              <p>
                <strong>ROMI</strong> stands for <strong>Rental Operations Maturity Index</strong>.
                Rather than a form to fill in, it's a short conversational assessment powered by
                Niva, our AI agent. Niva asks you about your portfolio size, how you spend your
                time, the tools you use, and where the friction actually is.
              </p>
              <p>
                At the end, you receive a <strong>ROMI Score from 0–100</strong>. The assessment
                evaluates your operation across multiple dimensions and derives a maturity level
                unique to your portfolio, ranging from fire-fighting daily to an operation that
                essentially runs itself.
              </p>
              <p>
                The report is immediate, personalised, and honest. It tells you not just where
                you are, but exactly what to change to reach the next tier.
              </p>
              <Link
                to="/niva-romi-score"
                className="inline-flex items-center gap-2 text-cyan-600 font-semibold hover:text-cyan-700 transition-colors"
              >
                Take the assessment now <ChevronRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Score card preview */}
            <div className="bg-gray-950 rounded-2xl p-6 text-white shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs text-gray-400 uppercase tracking-widest font-semibold">Your ROMI Score™</span>
                <span className="text-xs bg-cyan-500/20 text-cyan-300 border border-cyan-500/30 rounded-full px-2 py-0.5">Sample report</span>
              </div>
              <div className="text-7xl font-black text-cyan-400 mb-1">62</div>
              <div className="text-sm text-gray-400 mb-5">out of 100</div>
              <div className="inline-flex items-center gap-2 bg-yellow-900/40 text-yellow-300 border border-yellow-700/50 rounded-full px-3 py-1 text-sm font-semibold mb-6">
                Managed Tier
              </div>
              <div className="space-y-2.5">
                {[
                  { label: 'Tenant Communication',  pct: 70 },
                  { label: 'Maintenance Management', pct: 55 },
                  { label: 'Financial Operations',   pct: 80 },
                  { label: 'Compliance & Docs',      pct: 50 },
                  { label: 'Technology Adoption',    pct: 45 },
                ].map(({ label, pct }) => (
                  <div key={label}>
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>{label}</span><span>{pct}/100</span>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Beta Case Studies ──────────────────────────────────────────────── */}
      <section className="py-20 px-4 bg-gray-950 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-4">
            <span className="text-xs text-cyan-400 uppercase tracking-widest font-semibold">Beta Tester Results</span>
          </div>
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What Landlords Discovered</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Real assessments from our beta programme: Central Ohio landlords who took the ROMI Score, discovered the gaps in their operation, and used NivasaPro.ai to close them.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {CASE_STUDIES.map(({ initials, name, location, units, tier, tierColor, score, quote, result, stars }) => (
              <div key={name} className="bg-gray-900 border border-gray-800 rounded-2xl p-6 flex flex-col">
                {/* Stars */}
                <div className="flex gap-0.5 mb-4">
                  {[...Array(stars)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
                  ))}
                </div>

                {/* Quote */}
                <p className="text-gray-300 text-sm leading-relaxed italic flex-1 mb-5">
                  "{quote}"
                </p>

                {/* Result callout */}
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg px-3 py-2 mb-5">
                  <p className="text-cyan-300 text-xs font-semibold flex items-center gap-1.5">
                    <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />{result}
                  </p>
                </div>

                {/* Profile */}
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                    {initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-xs font-semibold">{name}</p>
                    <p className="text-gray-500 text-xs">{location} · {units}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white text-xs font-black">{score}<span className="text-gray-500 font-normal">/100</span></p>
                    <p className={`text-xs font-semibold ${tierColor}`}>{tier}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Aggregate trust signals */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { n: '54+',  label: 'Assessments completed' },
              { n: '4.9',  label: 'Average rating'         },
              { n: '3 min', label: 'Average completion time' },
              { n: '100%',  label: 'Free, no card required' },
            ].map(({ n, label }) => (
              <div key={label} className="text-center bg-gray-900 border border-gray-800 rounded-xl py-4 px-3">
                <p className="text-2xl font-black text-cyan-400 mb-1">{n}</p>
                <p className="text-xs text-gray-400">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ──────────────────────────────────────────────────── */}
      <section id="how-it-works" className="py-20 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-gray-600">Three steps. No forms. No friction.</p>
          </div>
          <div className="space-y-6">
            {STEPS.map(({ n, title, desc }) => (
              <div key={n} className="flex gap-5 items-start">
                <div className="h-10 w-10 rounded-full bg-cyan-600 text-white font-black text-sm flex items-center justify-center flex-shrink-0">
                  {n}
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900 mb-1">{title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/niva-romi-score"
              className="inline-flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-semibold px-8 py-4 rounded-xl text-base transition-colors shadow-sm"
            >
              Start My Free Assessment <ArrowRight className="h-5 w-5" />
            </Link>
            <p className="text-xs text-gray-400 mt-3">No account required · Results in under 4 minutes</p>
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────────────────── */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-10 text-center">Frequently Asked Questions</h2>
          <div className="space-y-5">
            {FAQS.map(({ q, a }) => (
              <div key={q} className="bg-white rounded-xl border border-gray-200 px-5 py-4">
                <h3 className="text-sm font-bold text-gray-900 mb-2">{q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section className="py-20 px-4 bg-gradient-to-r from-cyan-600 to-blue-700 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <BarChart2 className="h-12 w-12 mx-auto mb-5 opacity-80" />
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to find out where you stand?
          </h2>
          <p className="text-cyan-100 text-lg mb-8 max-w-xl mx-auto">
            Join 54+ landlords who've taken the ROMI Score™ assessment.
            Free, instant, and genuinely useful, regardless of whether you ever use NivasaPro.
          </p>
          <Link
            to="/niva-romi-score"
            className="inline-flex items-center gap-2 bg-white text-cyan-700 hover:bg-cyan-50 font-bold px-8 py-4 rounded-xl text-base transition-colors shadow-lg"
          >
            Get My Free ROMI Score™ <ArrowRight className="h-5 w-5" />
          </Link>
          <p className="text-cyan-200 text-xs mt-4">
            Already taken it?{' '}
            <Link to="/blog/pm-health-check" className="underline hover:text-white transition-colors">
              Read more about how the ROMI Score works →
            </Link>
          </p>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="py-8 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-400">
          <span>© 2026 NivasaPro.ai · AI Property Management Software</span>
          <div className="flex gap-4">
            <Link to="/privacy" className="hover:text-gray-700 transition-colors">Privacy</Link>
            <Link to="/terms"   className="hover:text-gray-700 transition-colors">Terms</Link>
            <Link to="/blog"    className="hover:text-gray-700 transition-colors">Blog</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default RomiScorePage;
